import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";

export type Director = Tables<"team_film_directors">;
export type DirectorInsert = TablesInsert<"team_film_directors">;

// Extended type with user data (first_name, last_name, email, avatar_url from users table)
export interface DirectorWithUserData extends Omit<Director, 'first_name' | 'last_name' | 'email'> {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
}

export const getDirectors = async () => {
  try {
    const { data, error } = await supabase
      .from("team_film_directors")
      .select("*, users!film_directors_user_id_fkey(first_name, last_name, email, avatar_url)")
      .order("user_id", { ascending: true });

    if (error) throw error;
    
    // Map user data to director level
    const mappedData = (data || []).map(director => ({
      ...director,
      first_name: (director as any).users?.first_name || null,
      last_name: (director as any).users?.last_name || null,
      email: (director as any).users?.email || null,
      avatar_url: (director as any).users?.avatar_url || null,
    }));
    
    // Sort by last_name after mapping
    mappedData.sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''));
    
    return { success: true, data: mappedData };
  } catch (error) {
    console.error("Error fetching directors:", error);
    return { success: false, error };
  }
};

export const getDirectorById = async (id: string) => {
  try {
    const { data, error } = await supabase
      .from("team_film_directors")
      .select("*, users!film_directors_user_id_fkey(first_name, last_name, email, avatar_url, cal_username)")
      .eq("id", id)
      .single();

    if (error) throw error;
    
    // Map user data to director level
    const result = data ? {
      ...data,
      first_name: (data as any).users?.first_name || null,
      last_name: (data as any).users?.last_name || null,
      email: (data as any).users?.email || null,
      avatar_url: (data as any).users?.avatar_url || null,
      cal_username: (data as any).users?.cal_username || null,
    } : null;
    
    return { success: true, data: result };
  } catch (error) {
    console.error("Error fetching director:", error);
    return { success: false, error };
  }
};

export const findDirectorByEmail = async (email: string) => {
  try {
    const { data, error } = await supabase
      .from("team_film_directors")
      .select("id, studio_id, user_id, location_id, users!film_directors_user_id_fkey(first_name, last_name, email, avatar_url)")
      .eq("users.email", email)
      // In case duplicates exist, pick the most recent
      .order("created_at", { ascending: false })
      .limit(1)
      .single();
    
    if (error) throw error;
    
    // Map user data to director level
    const result = data ? {
      ...data,
      first_name: (data as any).users?.first_name || null,
      last_name: (data as any).users?.last_name || null,
      email: (data as any).users?.email || null,
      avatar_url: (data as any).users?.avatar_url || null,
    } : null;
    
    return { success: true, data: result };
  } catch (error) {
    console.error("Error finding director by email:", error);
    return { success: false, error };
  }
};

export const getDirectorsByLocation = async (locationId: string) => {
  try {
    const { data, error } = await supabase
      .from("team_film_directors")
      .select("*, studios(*), users!film_directors_user_id_fkey(first_name, last_name, email, avatar_url)")
      .eq("location_id", locationId);

    if (error) throw error;
    
    // Map user data to director level
    const mappedData = (data || []).map(director => ({
      ...director,
      first_name: (director as any).users?.first_name || null,
      last_name: (director as any).users?.last_name || null,
      email: (director as any).users?.email || null,
      avatar_url: (director as any).users?.avatar_url || null,
    }));
    
    return { success: true, data: mappedData };
  } catch (error) {
    console.error("Error fetching directors by location:", error);
    return { success: false, error };
  }
};